import closeIcon from "@loot-os/icons/icons/close";
import { Button } from "../../../../core/Button.js";
import { IconButton } from "../../../../core/IconButton.js";
import { Input } from "../../../../core/Input.js";
import { Panel } from "../../../../core/Panel.js";
import { Text } from "../../../../core/Text.js";
import { Amount } from "../../../../domain/Amount.js";
import { ItemArt } from "../../../../domain/ItemArt.js";
import type { ForgeRatesContent } from "../../forgeContent.js";

const TOTAL_TONE = { complete: "success", empty: "tertiary", off: "danger" } as const;

/**
 * Step 4: each picked item's pull rate, what it adds to the price, and the total that has to
 * reach 100%. Rows sort by value and scroll inside the step (13 · Exchange, Checkout's rows).
 */
export function ForgeRates({
  step,
  title,
  description,
  currency,
  total,
  empty,
  sort,
  rows,
}: ForgeRatesContent) {
  return (
    <Panel
      size="sm"
      title={title}
      eyebrow={step}
      description={description}
      end={
        <Text
          as="p"
          variant="mono-price"
          tone={TOTAL_TONE[total.tone]}
          aria-label={`${total.label} ${total.text}`}
          className="shrink-0 tabular-nums"
        >
          {total.text}
        </Text>
      }
    >
      {rows.length === 0 ? (
        <Text variant="body-b2" tone="secondary">
          {empty}
        </Text>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-end gap-1">
            <Text as="span" variant="body-b3" tone="tertiary" className="mr-1">
              {sort.label}
            </Text>
            {[sort.ascending, sort.descending].map((action) => (
              <Button key={action.label} variant="ghost" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ))}
          </div>
          <ul className="flex max-h-[28rem] flex-col gap-2 overflow-y-auto pr-1">
            {rows.map((row) => (
              <li
                key={row.id}
                className="grid grid-cols-[3rem_minmax(0,1fr)_auto] items-center gap-3 rounded-xl bg-bg-raised p-2"
              >
                <span className="block rounded-md bg-bg-inset p-1">
                  <ItemArt src={row.image} alt="" shape="square" />
                </span>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-baseline justify-between gap-2">
                    <Text as="span" variant="body-b2-strong" tone="primary" className="truncate">
                      {row.name}
                    </Text>
                    <Amount cents={row.value} currency={currency} size="meta" />
                  </span>
                  <span className="flex items-center gap-2">
                    <Input
                      label={row.percent.label}
                      hideLabel
                      mono
                      size="sm"
                      inputMode="decimal"
                      placeholder={row.percent.placeholder}
                      value={row.percent.value}
                      onChange={(event) => row.percent.onChange(event.target.value)}
                      trailing={
                        <Text as="span" variant="body-b3" tone="tertiary">
                          %
                        </Text>
                      }
                      className="w-28"
                    />
                    <span className="rounded-sm bg-bg-inset px-1.5 py-0.5 font-mono text-mono-meta text-text-secondary">
                      +<Amount cents={row.contribution} currency={currency} size="meta" />
                    </span>
                  </span>
                </div>
                <IconButton
                  icon={closeIcon}
                  label={row.remove.label}
                  size="sm"
                  onClick={row.remove.onRemove}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}
